import React, { useEffect, useState } from "react";
import { supabase } from "./config/supabase";

const NAVBAR_HEIGHT = 80;

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    const y =
      el.getBoundingClientRect().top + window.pageYOffset - NAVBAR_HEIGHT;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const LandingPage: React.FC = () => {
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Back to top button state
  const [showBackToTop, setShowBackToTop] = useState(false);
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Mobile menu toggle
  const handleMobileMenuToggle = () => setMobileMenuOpen((open) => !open);
  const handleMobileMenuLinkClick = (id: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => scrollToSection(id), 100); // Wait for menu to close
  };
  const handleNavLinkClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    scrollToSection(id);
  };

  // Back to top logic
  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Scroll-triggered fade-in-up animations
  useEffect(() => {
    const elements = document.querySelectorAll(".fade-in-up");
    const observer = new window.IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "0px", threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Contact form handler
  const handleContactChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setContactForm({ ...contactForm, [e.target.name]: e.target.value });
  };
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus(null);
    setFormLoading(true);
    // Basic validation
    if (contactForm.name.length < 2) {
      setFormStatus("Name must be at least 2 characters long.");
      setFormLoading(false);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactForm.email.trim())) {
      setFormStatus("Please enter a valid email address.");
      setFormLoading(false);
      return;
    }
    if (contactForm.message.length < 10) {
      setFormStatus("Message must be at least 10 characters long.");
      setFormLoading(false);
      return;
    }
    try {
      const { error } = await supabase.from("messages").insert([
        {
          name: contactForm.name.trim(),
          email: contactForm.email.trim().toLowerCase(),
          message: contactForm.message.trim(),
          created_at: new Date().toISOString(),
        },
      ]);
      if (error) {
        console.error("Contact form error:", error);
        setFormStatus("Failed to send message. Please try again later.");
      } else {
        setFormStatus("Message sent successfully! We'll get back to you soon.");
        setContactForm({ name: "", email: "", message: "" });
      }
    } catch (err: any) {
      console.error("Contact form exception:", err);
      setFormStatus("Failed to send message. Please try again later.");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-md py-4 px-6 md:px-12 fixed w-full z-[1000] top-0">
        <nav className="container mx-auto flex justify-between items-center">
          <a
            href="#home"
            className="text-2xl font-bold text-brand-dark-blue rounded-md p-2 hover:bg-gray-100 transition-colors"
            onClick={handleNavLinkClick("home")}
          >
            <img
              src="/assets/Amblify_logo_zilsfix.png"
              alt="Amblyfy"
              className="h-10"
            />
          </a>
          <div className="hidden md:flex space-x-6">
            <a
              href="#home"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
              onClick={handleNavLinkClick("home")}
            >
              Home
            </a>
            <a
              href="#social-proof"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
              onClick={handleNavLinkClick("social-proof")}
            >
              Testimonials
            </a>
            <a
              href="#about"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
              onClick={handleNavLinkClick("about")}
            >
              Features
            </a>
            <a
              href="#contact"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
              onClick={handleNavLinkClick("contact")}
            >
              Contact
            </a>
            <div className="flex items-center space-x-3 ml-4">
              <a
                href="/login"
                className="text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md px-4 py-2 shadow-md hover:scale-105 duration-200"
              >
                Log In
              </a>
              <a
                href="/login?register=1"
                className="text-brand-dark-blue/80 border border-brand-dark-blue/60 hover:border-brand-dark-blue hover:text-brand-dark-blue font-semibold transition-colors rounded-md px-4 py-2 shadow-sm"
              >
                Register
              </a>
            </div>
          </div>
          <button
            id="mobile-menu-button"
            className="md:hidden text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-dark-blue rounded-md p-2"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </nav>
        <div
          id="mobile-menu"
          className={`md:hidden bg-white py-2 mt-2 border-t border-gray-200${
            mobileMenuOpen ? "" : " hidden"
          }`}
        >
          <a
            href="#home"
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
            onClick={() => handleMobileMenuLinkClick("home")}
          >
            Home
          </a>
          <a
            href="#social-proof"
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
            onClick={() => handleMobileMenuLinkClick("social-proof")}
          >
            Testimonials
          </a>
          <a
            href="#about"
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
            onClick={() => handleMobileMenuLinkClick("about")}
          >
            Features
          </a>
          <a
            href="#contact"
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
            onClick={() => handleMobileMenuLinkClick("contact")}
          >
            Contact
          </a>
          <a
            href="/login"
            className="block px-4 py-2 text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md mx-2 mt-2 shadow-md text-center"
            onClick={() => handleMobileMenuLinkClick("login")}
          >
            Log In
          </a>
        </div>
      </header>
      {/* Main Content */}
      <main className="pt-20">
        {/* Home Section */}
        <section
          id="home"
          className="bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green text-white py-20 px-6 md:px-12 text-center flex items-center justify-center min-h-[calc(100vh-80px)] relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-20 h-20 bg-white rounded-full opacity-20"></div>
            <div className="absolute top-40 right-20 w-16 h-16 bg-brand-light-green rounded-full opacity-30"></div>
            <div className="absolute bottom-20 left-20 w-24 h-24 bg-white rounded-full opacity-15"></div>
            <div className="absolute bottom-40 right-10 w-12 h-12 bg-brand-light-green rounded-full opacity-25"></div>
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            <h1 className="text-5xl md:text-7xl font-extrabold leading-tight mb-8 bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm shadow-2xl">
              Smart Amblyopia
              <span className="block text-brand-light-green">
                Treatment Tracking
              </span>
            </h1>

            <p className="text-xl md:text-2xl mb-10 opacity-95 max-w-3xl mx-auto leading-relaxed">
              Professional-grade app connecting families with healthcare
              providers for effective amblyopia treatment management. Features
              intelligent timer tracking, comprehensive progress calendars, and
              real-time doctor connectivity.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12 max-w-4xl mx-auto">
              <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-light-green rounded-full flex items-center justify-center">
                  <span className="text-xl">⏱️</span>
                </div>
                <span className="font-semibold">
                  Precision timer for treatment sessions
                </span>
              </div>
              <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-light-green rounded-full flex items-center justify-center">
                  <span className="text-xl">📅</span>
                </div>
                <span className="font-semibold">
                  Visual progress calendar with doctor access
                </span>
              </div>
              <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-light-green rounded-full flex items-center justify-center">
                  <span className="text-xl">📚</span>
                </div>
                <span className="font-semibold">
                  Evidence-based treatment guides & research
                </span>
              </div>
              <div className="bg-white bg-opacity-15 rounded-xl p-4 backdrop-blur-sm flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-light-green rounded-full flex items-center justify-center">
                  <span className="text-xl">🏥</span>
                </div>
                <span className="font-semibold">
                  Healthcare provider dashboard integration
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="#social-proof"
                className="inline-flex items-center justify-center bg-white text-brand-dark-blue font-bold py-4 px-10 rounded-full shadow-2xl hover:bg-brand-light-green hover:scale-105 transition-all duration-300 ease-in-out transform group"
                onClick={handleNavLinkClick("social-proof")}
              >
                See What Parents Say
                <span className="ml-2 group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </a>
            </div>
          </div>
        </section>
        {/* Social Proof Section */}
        <section
          id="social-proof"
          className="py-20 px-6 md:px-12 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-brand-cyan/5 to-brand-dark-green/5"></div>

          <div className="container mx-auto text-center relative z-10">
            <div className="mb-16">
              <div className="inline-flex items-center bg-brand-cyan/10 text-brand-dark-blue px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Early User Feedback
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark-blue mb-4 fade-in-up">
                What Parents Are Saying
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Feedback from parents during our testing phase who understand
                the challenges of amblyopia treatment
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="group bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-t-4 border-brand-cyan fade-in-up transform hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-brand-cyan/10 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">👩‍👧‍👦</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">
                      Parent of 5-year-old
                    </div>
                  </div>
                </div>
                <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                  "This app is exactly what I've been missing. I always forget
                  to patch, and it's hard to track what we've done. I would 100%
                  use this."
                </blockquote>
                <div className="text-brand-dark-blue font-semibold text-sm">
                  — Early feedback from testing group
                </div>
              </div>

              <div className="group bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-t-4 border-brand-dark-green fade-in-up transform hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-brand-dark-green/10 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">👨‍👧</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Busy parent</div>
                  </div>
                </div>
                <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                  "The idea of being able to reschedule missed patching time
                  sounds amazing. Life is busy — this would really help."
                </blockquote>
                <div className="text-brand-dark-blue font-semibold text-sm">
                  — Parent in early testing group
                </div>
              </div>

              <div className="group bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-t-4 border-brand-light-green fade-in-up transform hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-brand-light-green/10 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">👩‍👧</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">
                      Support group member
                    </div>
                  </div>
                </div>
                <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                  "Our daughter hates patching, but if there were rewards or
                  games, she'd actually enjoy it. This could make a huge
                  difference."
                </blockquote>
                <div className="text-brand-dark-blue font-semibold text-sm">
                  — Feedback from amblyopia support group
                </div>
              </div>

              <div className="group bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border-t-4 border-brand-pale-green fade-in-up transform hover:-translate-y-2">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-brand-pale-green/10 rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">👩‍⚕️</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">
                      Survey respondent
                    </div>
                  </div>
                </div>
                <blockquote className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                  "Tracking patch time and showing it to the doctor would take
                  so much stress off me. This is something parents really need."
                </blockquote>
                <div className="text-brand-dark-blue font-semibold text-sm">
                  — Survey response, May 2025
                </div>
              </div>
            </div>

            <div className="mt-16">
              <div className="inline-flex items-center bg-gradient-to-r from-brand-cyan to-brand-dark-green text-white px-8 py-3 rounded-full font-semibold shadow-lg">
                <span className="mr-2">✨</span>
                Join hundreds of families already using Amblyfy
              </div>
            </div>
          </div>
        </section>
        {/* Features Section */}
        <section
          id="about"
          className="py-20 px-6 md:px-12 bg-gradient-to-br from-brand-cyan via-brand-dark-green to-brand-dark-blue relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 right-20 w-32 h-32 bg-white rounded-full"></div>
            <div className="absolute bottom-20 left-20 w-24 h-24 bg-brand-light-green rounded-full"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white rounded-full"></div>
          </div>

          <div className="container mx-auto relative z-10">
            <div className="max-w-6xl mx-auto text-center fade-in-up">
              <div className="mb-12">
                <div className="inline-flex items-center bg-white/20 text-white px-6 py-3 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm">
                  Core Features
                </div>
                <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                  Making Amblyopia Treatment
                  <span className="block text-brand-light-green">
                    More Effective
                  </span>
                </h2>
                <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
                  Our professional-grade app combines smart technology with
                  proven treatment methods to help families stay consistent and
                  healthcare providers stay connected.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-brand-light-green rounded-xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">⏰</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    Smart Reminders
                  </h3>
                  <p className="text-white/80">
                    Intelligent notifications that adapt to your family's
                    schedule and never let you miss a session.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-brand-light-green rounded-xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">📊</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    Progress Tracking
                  </h3>
                  <p className="text-white/80">
                    Automatic time tracking with beautiful visual reports that
                    doctors love to see.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-brand-light-green rounded-xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">🔄</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    Flexible Scheduling
                  </h3>
                  <p className="text-white/80">
                    Life happens. Easily reschedule missed sessions without
                    losing track of your progress.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-brand-light-green rounded-xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">🎮</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    Kid-Friendly Rewards
                  </h3>
                  <p className="text-white/80">
                    Gamification that makes patching fun with achievements,
                    badges, and motivational milestones.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-brand-light-green rounded-xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">👩‍⚕️</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    Doctor Dashboard
                  </h3>
                  <p className="text-white/80">
                    Real-time connectivity allowing healthcare providers to
                    monitor patient progress remotely.
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-brand-light-green rounded-xl flex items-center justify-center mx-auto mb-6">
                    <span className="text-3xl">📚</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    Evidence-Based Guides
                  </h3>
                  <p className="text-white/80">
                    Access to the latest research, treatment guides, and expert
                    recommendations built right in.
                  </p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 mb-12">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Built for Real-World Treatment Challenges
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl font-bold text-brand-light-green mb-2">
                      2-4 hrs
                    </div>
                    <div className="text-white/80">
                      Daily patching made easier
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-brand-light-green mb-2">
                      6-18 mo
                    </div>
                    <div className="text-white/80">
                      Typical treatment duration
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-brand-light-green mb-2">
                      24/7
                    </div>
                    <div className="text-white/80">
                      Progress tracking & support
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a
                  href="#contact"
                  className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white font-bold py-4 px-10 rounded-full shadow-2xl hover:bg-white hover:text-brand-dark-blue hover:scale-105 transition-all duration-300 ease-in-out transform group backdrop-blur-sm"
                  onClick={handleNavLinkClick("contact")}
                >
                  Contact Sales
                  <span className="ml-2 group-hover:translate-x-1 transition-transform">
                    →
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>
        {/* Contact Section */}
        <section
          id="contact"
          className="py-20 px-6 md:px-12 bg-gradient-to-br from-gray-50 to-white"
        >
          <div className="container mx-auto text-center max-w-4xl">
            <div className="mb-12">
              <div className="inline-flex items-center bg-brand-cyan/10 text-brand-dark-blue px-4 py-2 rounded-full text-sm font-semibold mb-4">
                💬 Ready to Get Started?
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark-blue mb-6 fade-in-up">
                Get in Touch
              </h2>
              <p
                className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto fade-in-up"
                style={{ transitionDelay: "0.1s" }}
              >
                Have questions about our professional amblyopia treatment
                platform? We're here to help you get started.
              </p>
            </div>
            <form
              id="contactForm"
              className="space-y-8 bg-white p-10 rounded-2xl shadow-2xl border border-gray-200 fade-in-up max-w-2xl mx-auto"
              style={{ transitionDelay: "0.2s" }}
              onSubmit={handleContactSubmit}
            >
              {formStatus && (
                <div
                  className={`p-4 mb-4 rounded-md ${
                    formStatus.startsWith("Message sent")
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {formStatus}
                </div>
              )}
              <div>
                <label
                  htmlFor="name"
                  className="block text-left text-brand-dark-blue text-sm font-semibold mb-2"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  maxLength={100}
                  pattern="[A-Za-z\s\-']{2,100}"
                  title="Please enter a valid name (2-100 characters, letters, spaces, hyphens, and apostrophes only)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-dark-blue"
                  placeholder="Your Name"
                  required
                  value={contactForm.name}
                  onChange={handleContactChange}
                />
                <span className="text-sm text-gray-500 mt-1 block">
                  Maximum 100 characters
                </span>
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-left text-brand-dark-blue text-sm font-semibold mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  maxLength={254}
                  pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                  title="Please enter a valid email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-dark-blue"
                  placeholder="your@example.com"
                  required
                  value={contactForm.email}
                  onChange={handleContactChange}
                />
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-left text-brand-dark-blue text-sm font-semibold mb-2"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={1000}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-dark-blue"
                  placeholder="Your Message"
                  required
                  value={contactForm.message}
                  onChange={handleContactChange}
                ></textarea>
                <span className="text-sm text-gray-500 mt-1 block">
                  Maximum 1000 characters
                </span>
              </div>
              <button
                type="submit"
                id="submitButton"
                className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-4 px-8 rounded-full shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 ease-in-out transform disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={formLoading}
              >
                <span className="inline-flex items-center justify-center">
                  {formLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <span className="mr-2">📧</span>
                      Send Message
                      <span className="ml-2">→</span>
                    </>
                  )}
                </span>
              </button>
            </form>
          </div>
        </section>
      </main>
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          id="back-to-top"
          className="fixed bottom-8 right-8 bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 focus:outline-none focus:ring-4 focus:ring-brand-cyan/50 transition-all duration-300 transform backdrop-blur-sm"
          onClick={handleBackToTop}
          aria-label="Back to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 w-full">
        <div className="w-full px-4">
          <div className="max-w-7xl mx-auto flex flex-col items-center space-y-4">
            <div className="flex space-x-6">
              {/* Social icons (SVGs) can be copied here as in index.html */}
            </div>
            <p className="text-gray-600">
              &copy; 2025 Amblyfy. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a
                href="/privacy-policy"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/evidence-statement"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Evidence Statement
              </a>
              <a
                href="/activity-tracking-guide"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Activity Tracking Guide
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;
